import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// shadcn form primitives
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { useMediaQueryLG } from '@/hooks/useMediaquery'
import { loginUser } from '@/api/authApi'
import { tokenService } from '@/lib/token.service'
import axios from 'axios'
import { EyeCloseIcon, EyeOpenIcon } from '@/components/Icons'
import { showErrorToast, showSuccessToast } from '@/components/ui/toast'
import { PATHROUTES } from '@/constants/path'
import { DEFAULT_USER, type User } from '@/schemas/user.schema'

// NOTE: You asked to keep the API call separate.
// Implement this in your codebase; here we only import and use it.

// If you have a token service utility, import it here.
// Ensure the function names match your implementation.

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .refine(val => /\.[a-z]{2,}$/i.test(val), {
      message: 'Email must end with a valid domain',
    }),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  // rememberMe: z.boolean().default(false),
})

type LoginFormData = z.infer<typeof loginSchema>

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useMediaQueryLG()
  const [showPassword, setShowPassword] = useState(false)

  const redirectUrl = searchParams.get('redirect')

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  })

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: LoginFormData) => {
      return await loginUser({
        email: values.email,
        password: values.password,
        loginFromAdmin: true,
      })
    },
    onSuccess: (res: { userInfo?: Partial<User> }) => {
      const user = res?.userInfo ?? DEFAULT_USER
      showSuccessToast('Logged in successfully')
      tokenService.saveUser(user)

      const userRoles = user.userRoles || []
      if (userRoles.length === 1) {
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true })
          return
        }
        navigate(PATHROUTES.DASHBOARD.ROOT, { replace: true })
      } else if (userRoles.length > 1) {
        const finalUrl = redirectUrl
          ? `${PATHROUTES.AUTH.ROLE_SELECTION}?redirect=${redirectUrl}`
          : PATHROUTES.AUTH.ROLE_SELECTION
        navigate(finalUrl, { replace: true })
      } else {
        console.error('No roles assigned to user')
      }
    },
    onError: error => {
      if (axios.isAxiosError(error) && error.response) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Login failed. Please try again.'
        console.error('Login Error:', message)
        showErrorToast(message)
      }
    },
  })

  const onSubmit = (values: LoginFormData) => {
    mutate(values)
  }

  return (
    <main className="w-full" aria-label="Login page">
      <header className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 w-full">
        <img
          src="/auth/logo.svg"
          alt="SlopeSync logo"
          className="w-28 sm:w-48"
        />
        <div className="flex flex-col items-center lg:items-start gap-2 sm:gap-3 w-full">
          <div className="text-center lg:text-left">
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-gray-950 uppercase font-[ClashDisplay]">
              DRIVE YOUR
            </h1>
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-blue-500 uppercase font-[ClashDisplay]">
              EVENTS FORWARD
            </h1>
          </div>
          <p className="text-sm lg:text-base font-semibold text-ss-gray-600 max-w-sm text-center lg:text-left">
            Log in to streamline registrations, schedules, and ticketing.
          </p>
        </div>
      </header>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col items-center gap-5 sm:gap-6 w-full"
          aria-label="Login form"
          autoComplete="off"
        >
          <div className="flex flex-col gap-4 sm:gap-5 w-full">
            {/* Email */}
            <div className="flex flex-col gap-1.5 w-full">
              <label
                htmlFor="email"
                className="text-sm leading-6 font-medium text-secondary-foreground"
              >
                Email
              </label>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        inputSize={isMobile ? 'small' : 'large'}
                        aria-invalid={!!form.formState.errors.email}
                        aria-describedby={
                          form.formState.errors.email
                            ? 'email-error'
                            : undefined
                        }
                        aria-required="true"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage
                      className="text-sm text-ss-text-critical"
                      id="email-error"
                      role="alert"
                    />
                  </FormItem>
                )}
              />
            </div>
            {/* Password */}
            <div className="flex flex-col gap-1.5 w-full">
              <label
                htmlFor="password"
                className="text-sm leading-6 font-medium text-secondary-foreground"
              >
                Password
              </label>
              <div className="relative">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <>
                      <FormItem>
                        <FormControl>
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            inputSize={isMobile ? 'small' : 'large'}
                            className={cn(
                              'pr-10',
                              form.formState.errors.password &&
                                'border-destructive focus-visible:border-destructive focus-visible:ring-ss-danger-300/20 '
                            )}
                            aria-invalid={!!form.formState.errors.password}
                            aria-describedby={
                              form.formState.errors.password
                                ? 'password-error'
                                : undefined
                            }
                            aria-required="true"
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-ss-gray-400 hover:text-muted-foreground"
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showPassword ? (
                            <EyeCloseIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          ) : (
                            <EyeOpenIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                        {/* Keep visual style identical */}
                      </FormItem>
                      <FormMessage
                        className="text-sm text-ss-text-critical absolute -bottom-6"
                        id="password-error"
                        role="alert"
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          <Link
            to="/auth/reset-password"
            className="text-sm font-semibold text-ss-blue-500 hover:text-ss-blue-600 text-right sm:text-right flex justify-end w-full"
            aria-label="Forgot Password?"
          >
            Forgot Password?
          </Link>
          <Button
            type="submit"
            disabled={!form.formState.isValid || isPending}
            size={isMobile ? 'small' : 'large'}
            className="w-full"
            aria-label="Log In"
          >
            {isPending ? 'Signing In...' : 'Log In'}
          </Button>
        </form>
      </Form>
    </main>
  )
}
